import { Lock, Printer, CalendarCheck, PackageCheck, ClipboardList, ShieldQuestion } from "lucide-react";
import { Button } from "@/components/ui/button";

const TERMS = [
  { icon: CalendarCheck, title: "One month, free", text: "No upfront cost, ever, during the pilot." },
  { icon: PackageCheck, title: "Minimum 10 locks", text: "Delivered and collected by us, with a lending log and guest signage." },
  { icon: ClipboardList, title: "Fixed dates in writing", text: "Start and end dates agreed before anything ships." },
  { icon: ShieldQuestion, title: "No obligation", text: "Hand the locks back at the end. That's the whole commitment." },
];

const ANSWERS = [
  {
    q: "Liability",
    a: "The locks are an as-is guest amenity, like lending an umbrella. Your hotel bears no added liability — in writing, in the pilot agreement.",
  },
  {
    q: "Lost or unreturned locks",
    a: "The guest's refundable holding deposit covers replacement, and during the pilot we replace lost stock at no cost to you.",
  },
  {
    q: "Holding deposits",
    a: "Your desk takes a small refundable deposit — card pre-auth or cash — and returns it when the lock comes back. Manual, no software.",
  },
  {
    q: "An honest note on theft",
    a: "A visible lock deters opportunistic drive-off and relay theft. It won't stop a tow truck — and we say so, to you and to guests.",
  },
];

export default function OnePager() {
  const applyUrl = `${window.location.origin}/#apply`;

  return (
    <div
      data-testid="one-pager-page"
      className="flex min-h-screen flex-col items-center bg-slate-100 px-6 py-12 print:bg-white print:p-0"
    >
      <div className="mb-8 flex flex-col items-center gap-3 print:hidden">
        <Button
          data-testid="print-one-pager-btn"
          onClick={() => window.print()}
          className="group rounded-full bg-accent px-8 font-semibold text-accent-foreground hover:bg-blue-500 transition-colors"
        >
          <Printer className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5" />
          Print or save as PDF
        </Button>
        <p className="text-xs text-muted-foreground">
          Fits one A4 page — use your browser's "Save as PDF" to email it to a GM.
        </p>
      </div>

      <div
        data-testid="one-pager-sheet"
        className="w-full max-w-3xl rounded-lg border border-border bg-white p-10 md:p-12 shadow-sm print:max-w-none print:rounded-none print:border-0 print:shadow-none print:p-8"
      >
        <div className="flex items-center justify-between border-b border-border pb-5">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary">
              <Lock className="h-4 w-4 text-white" />
            </span>
            <p className="font-serif text-lg font-semibold tracking-tight text-primary">
              SecureStay <span className="italic text-accent">Locks</span>
            </p>
          </div>
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-muted-foreground">
            Pilot terms — one page
          </p>
        </div>

        <h1 className="mt-8 font-serif text-3xl md:text-4xl tracking-tight text-primary leading-tight">
          Give your guests free peace of mind for their car —{" "}
          <span className="italic text-accent">the pilot, on one page.</span>
        </h1>
        <p className="mt-4 text-sm md:text-base leading-relaxed text-muted-foreground">
          SecureStay Locks lends your guests a high-visibility steering wheel
          lock: a thoughtful, free amenity that adds a visible deterrent in your
          car park and sets your hotel apart. We are proving the model with a
          small group of London hotels — here is everything, up front.
        </p>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {TERMS.map((term, i) => (
            <div key={i} data-testid={`one-pager-term-${i}`} className="rounded-lg border border-border p-5">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 text-accent">
                  <term.icon className="h-4 w-4" />
                </span>
                <p className="font-serif text-base tracking-tight text-primary">{term.title}</p>
              </div>
              <p className="mt-2.5 text-xs md:text-sm leading-relaxed text-muted-foreground">{term.text}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-9 font-serif text-xl tracking-tight text-primary">How it works</h2>
        <ol className="mt-4 space-y-2.5 text-sm md:text-base text-foreground">
          <li className="flex gap-3"><span className="font-serif text-accent">1.</span> Apply in two minutes — tell us about your car park.</li>
          <li className="flex gap-3"><span className="font-serif text-accent">2.</span> Locks delivered to your door, with a front-desk lending log.</li>
          <li className="flex gap-3"><span className="font-serif text-accent">3.</span> Guests borrow at check-in against a small refundable holding deposit.</li>
        </ol>

        <h2 className="mt-9 font-serif text-xl tracking-tight text-primary">Straight answers</h2>
        <div className="mt-4 space-y-4">
          {ANSWERS.map((item, i) => (
            <div key={i} data-testid={`one-pager-answer-${i}`}>
              <p className="text-sm font-semibold text-primary">{item.q}</p>
              <p className="mt-1 text-xs md:text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-lg bg-slate-950 p-6 text-center">
          <p className="font-serif text-xl tracking-tight text-white">
            Ready when you are.
          </p>
          <p className="mt-2 text-sm text-slate-300">
            Apply in two minutes at{" "}
            <span className="font-semibold text-blue-300">{applyUrl}</span>
          </p>
          <p className="mt-1 text-xs text-slate-400">
            Personal reply within two working days. No card, no commitment.
          </p>
        </div>

        <p className="mt-6 text-[10px] leading-relaxed text-muted-foreground">
          SecureStay Locks is a guest amenity pilot for London hotels. Locks are
          a visible deterrent, provided as-is; hotels bear no added liability.
          © 2026 SecureStay Locks.
        </p>
      </div>
    </div>
  );
}
